import { useEffect, useState } from 'react';
import axios from 'axios';
import { Quote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PageQuote({ pageName, position = 1, variant = 'default' }) {
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    const loadQuote = async () => {
      try {
        const response = await axios.get(`${API}/quotes/${pageName}`);
        const quotes = response.data;
        const matchingQuote = quotes.find(q => q.position === position);
        setQuote(matchingQuote);
      } catch (error) {
        console.log(`No quote for ${pageName} position ${position}`);
      }
    };

    loadQuote();
  }, [pageName, position]);

  if (!quote) return null;

  if (variant === 'hero') {
    // Large hero-style quote
    return (
      <div className="relative py-16 overflow-hidden">
        {/* Retro Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#E85D4D]/20 via-[#E8934D]/20 to-[#E85D4D]/20"></div>
        <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(232,147,77,0.1)_10px,rgba(232,147,77,0.1)_20px)]"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="cassette-card p-8 md:p-12 relative">
            {/* Quote Icon */}
            <div className="absolute -top-6 -left-6 bg-gradient-to-br from-[#E8934D] to-[#E85D4D] rounded-full p-4 shadow-2xl border-4 border-[#F4E4C1]">
              <Quote className="w-8 h-8 text-[#F4E4C1]" />
            </div>
            
            {/* Quote Text */}
            <blockquote className="text-2xl md:text-3xl font-bold text-[#2C1810] italic leading-relaxed mb-4">
              "{quote.quote_text}"
            </blockquote>
            
            {/* Author */}
            {quote.author && (
              <p className="text-xl text-[#E8934D] font-semibold text-right">
                — {quote.author}
              </p>
            )}
            
            {/* Decorative Elements */}
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-[#E8934D]/20 to-transparent rounded-tl-full"></div>
          </div>
        </div>
      </div>
    );
  }

  // Compact inline quote
  return (
    <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="relative p-6 md:p-8 bg-gradient-to-br from-[#F4E4C1] to-[#E8B67A] rounded-lg shadow-lg border-4 border-[#6B4423]">
          {/* VHS scan lines effect */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="h-full w-full bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,#000_2px,#000_4px)]"></div>
          </div>
          
          <div className="relative z-10 flex gap-4">
            <Quote className="w-8 h-8 text-[#E85D4D] flex-shrink-0 mt-1" />
            <div>
              <p className="text-lg md:text-xl font-semibold text-[#2C1810] italic leading-relaxed mb-2">
                "{quote.quote_text}"
              </p>
              {quote.author && (
                <p className="text-base text-[#6B4423] font-bold">
                  — {quote.author}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
